import { AuthenticationResult, EventMessage, EventType, IPublicClientApplication } from "@azure/msal-browser";
import { clearTokenCache } from "../hooks/useAuthToken";
import { invalidateIdentityContext } from "./devImpersonation";

type IdentityClient = Pick<IPublicClientApplication,
  "getActiveAccount" | "getAllAccounts" | "setActiveAccount" | "addEventCallback" |
  "removeEventCallback" | "enableAccountStorageEvents" | "disableAccountStorageEvents">;

export function registerMsalIdentityEvents(
  instance: IdentityClient,
  reload = () => window.location.reload(),
) {
  const selectedAccount = () => instance.getActiveAccount() || instance.getAllAccounts()[0];
  const initial = selectedAccount();
  if (!instance.getActiveAccount() && initial) {
    instance.setActiveAccount(initial);
  }
  let accountId = selectedAccount()?.homeAccountId ?? null;
  const invalidateChangedAccount = () => {
    const next = selectedAccount()?.homeAccountId ?? null;
    if (next === accountId) return false;
    accountId = next;
    clearTokenCache();
    invalidateIdentityContext();
    return true;
  };

  instance.enableAccountStorageEvents();
  const callbackId = instance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.SSO_SILENT_SUCCESS) {
      const account = (event.payload as AuthenticationResult | null)?.account;
      if (account) {
        instance.setActiveAccount(account);
        invalidateChangedAccount();
      }
    }
  });
  const onStorage = (event: StorageEvent) => {
    if (event.storageArea !== window.localStorage || !invalidateChangedAccount()) return;
    // MSAL 2.x does not notify React when only its active-account cache key changes.
    // Restart the shell against the current SDK cache, discarding the old forms.
    reload();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("storage", onStorage);
    instance.disableAccountStorageEvents();
    if (callbackId) instance.removeEventCallback(callbackId);
  };
}
